// Copyright (c) 2026 Finerty Data Ltd. All rights reserved.
//
// This file is part of proprietary software owned by Finerty Data Ltd.
// It is confidential and may not be copied, distributed, modified, or used,
// in whole or in part, without the prior written consent of Finerty Data Ltd.
//
// Author: Ahmed Hosny

import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { AppIcon, Button, SearchBar, RejectionReasonModal } from '@loa/ui-components';
import { profileRequestApi } from '../../api/profileRequestApi';
import { providerSubmissionApi } from '../../api/providerSubmissionApi';
import { clientApi } from '../../api/clientApi';
import { getRejectionReasonLabel } from '../../utils/rejectionReasonDisplay';
import type {
  ProfileRequestDTO,
  ProfileRequestType,
} from '../../types/profileRequest';
import type { Policy } from '../../types/policy';
import { LOATableCard, TrashIcon, EyeIcon } from '../loa';
import type { LOATableColumn, LOATableAction } from '../loa';

// Provider submission types that carry a rejection justification — REJECTION
// for LOA, COA_REJECT for CoA (the COA_REJECT string isn't in the frontend
// ProviderSubmissionType union, so match against a plain string array).
const REJECTION_SUBMISSION_TYPES = ['REJECTION', 'COA_REJECT'];
import { RequestTypeBadge } from './RequestTypeBadge';
import { RequestDisplayStatusBadge, deriveDisplayStatus } from './RequestDisplayStatusBadge';
import { NudgeOutstandingButton } from './NudgeOutstandingButton';
import { ChooseRequestModal } from './ChooseRequestModal';
import { VoidRequestModal } from './VoidRequestModal';
import type { NudgeMode } from './ChooseRequestModal';
import { ownershipLabel, signatoryNamesFor, formatLastUpdated, clientIdForRequest } from './requestCells';
// Reuse the Provider / Policy-Type / Policy-Number cell styling from the
// Policies table so the Requests tab reads identically (see PolicyList.css
// selectors .policy-provider / .policy-type / .policy-number).
import '../PolicyList.css';
// Scoped overrides that align the Requests table (LOATableCard) with the
// Policies table (.policy-list-table) — header/cell padding + title font.
import './RequestsTab.css';

export interface RequestsTabProps {
  profileId: string;
  /**
   * Policies already loaded by the parent Relationship page. Used to
   * enrich each Request row with provider + policy number without a
   * separate fetch. Optional for callers that don't have them.
   */
  policies?: Policy[];
  /** Notifies the parent of the unfiltered Requests count so the tab
   * label can render its count badge. */
  onCountChange?: (count: number) => void;
}

/**
 * Profile-page tab listing every Profile Request on the profile —
 * signing and non-signing, policy-scoped and profile-scoped. Sits
 * alongside the unchanged "Client Signed Documents" tab and mirrors
 * its table card layout (LOATableCard) so both tabs read the same.
 */
export const RequestsTab: React.FC<RequestsTabProps> = ({ profileId, policies = [], onCountChange }) => {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const [requests, setRequests] = useState<ProfileRequestDTO[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [typeFilter, setTypeFilter] = useState<ProfileRequestType | undefined>(undefined);
  const [showSuccessBanner, setShowSuccessBanner] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  // Which actor the "Nudge Outstanding" chooser opened for (null = closed).
  const [nudgeMode, setNudgeMode] = useState<NudgeMode | null>(null);
  // Provider rejection-reason modal state (opened from the eye action on a
  // REJECTED row). null = closed.
  const [rejectionModal, setRejectionModal] = useState<{
    reason: string;
    additionalInformation: string | null;
    documentLabel: string;
  } | null>(null);
  // Request targeted by the delete (void) action; opens the red confirmation
  // modal that collects the reason. null = closed.
  const [voidTarget, setVoidTarget] = useState<ProfileRequestDTO | null>(null);

  const refresh = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const page = await profileRequestApi.list(profileId, {
        requestType: typeFilter,
      });
      setRequests(page.content);
      // Only report unfiltered count to the parent so the tab badge
      // reflects the total Requests on the profile, not the current view.
      if (!typeFilter) onCountChange?.(page.content.length);
    } catch (e: unknown) {
      const status = (e as { response?: { status?: number } })?.response?.status;
      if (status === 404) {
        setError('Profile Requests is not enabled for this tenant yet.');
      } else {
        setError(e instanceof Error ? e.message : 'Failed to load requests');
      }
      setRequests([]);
      if (!typeFilter) onCountChange?.(0);
    } finally {
      setLoading(false);
    }
  }, [profileId, typeFilter, onCountChange]);

  useEffect(() => {
    void refresh();
  }, [refresh]);

  // On mount, if we came back from the New Request wizard with
  // ?toast=requests-sent, show the green "Requests sent successfully!"
  // banner for 5s, then strip the query param so a manual refresh doesn't
  // re-trigger it.
  useEffect(() => {
    if (searchParams.get('toast') !== 'requests-sent') return;
    setShowSuccessBanner(true);
    const next = new URLSearchParams(searchParams);
    next.delete('toast');
    setSearchParams(next, { replace: true });
    const timer = globalThis.setTimeout(() => setShowSuccessBanner(false), 5000);
    return () => globalThis.clearTimeout(timer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const policyById = useMemo(() => {
    const map = new Map<string, Policy>();
    for (const p of policies) map.set(p.id, p);
    return map;
  }, [policies]);

  const filteredRequests = useMemo(() => {
    const q = searchQuery.trim().toLowerCase();
    if (!q) return requests;
    return requests.filter((r) => {
      const policy = r.policyId ? policyById.get(r.policyId) : null;
      const haystack = [
        r.requestType,
        policy?.providerName,
        policy?.carrier,
        policy?.policyTypeName,
        policy?.category,
        policy?.policyNumber,
      ]
        .filter(Boolean)
        .join(' ')
        .toLowerCase();
      return haystack.includes(q);
    });
  }, [requests, searchQuery, policyById]);


  // The delete action opens the red confirmation modal; the actual void runs
  // on confirm (handleVoidConfirm) once the advisor provides a reason.
  const onVoid = useCallback((r: ProfileRequestDTO) => {
    setVoidTarget(r);
  }, []);

  const handleVoidConfirm = useCallback(async (reason: string) => {
    if (!voidTarget) return;
    await profileRequestApi.void(voidTarget.id, { reason });
    await refresh();
    setVoidTarget(null);
  }, [voidTarget, refresh]);

  // Open the standardised Rejection Reason modal for a REJECTED request,
  // sourcing the justification the provider entered on the Open Access page.
  // The reason code + free text are persisted on the latest provider
  // submission (REJECTION for LOA, COA_REJECT for CoA) for the request's policy.
  const onViewRejection = useCallback(async (r: ProfileRequestDTO) => {
    const documentLabel = r.requestType === 'COA' ? 'Change of Agency' : 'Letter of Authority';
    if (!r.policyId) {
      setRejectionModal({ reason: '', additionalInformation: null, documentLabel });
      return;
    }
    try {
      const submissions = await providerSubmissionApi.listSubmissions(r.policyId);
      // Submissions come back ordered by createdAt desc, so the first match is latest.
      const rejection = submissions.find((s) =>
        REJECTION_SUBMISSION_TYPES.includes(s.submissionType),
      );
      const code = rejection?.formData?.rejectionReasonCode as string | undefined;
      setRejectionModal({
        reason: getRejectionReasonLabel(code),
        additionalInformation: rejection?.rejectionReason ?? null,
        documentLabel,
      });
    } catch {
      // On failure still open the modal so the advisor gets feedback rather
      // than a dead click; values fall back to the modal's "—" placeholders.
      setRejectionModal({ reason: '', additionalInformation: null, documentLabel });
    }
  }, []);

  // Open the policy detail page for an accepted (COMPLETE) request so the
  // advisor can review the data the provider returned. Mirrors the rejection
  // eye, but routes to the policy instead of opening a modal.
  const onViewPolicy = useCallback((r: ProfileRequestDTO) => {
    if (!r.policyId) return;
    navigate(`/policies/${r.policyId}`);
  }, [navigate]);

  // A request picked from the "Choose a request" chooser routes to the
  // existing per-request email page — the provider nudge page (LOA) or the
  // client resend-invitation page — so the advisor sends a single nudge.
  const handleNudgeSelect = useCallback(
    (request: ProfileRequestDTO, mode: NudgeMode) => {
      setNudgeMode(null);
      if (mode === 'PROVIDER') {
        if (!request.policyId) return;
        navigate(`/relationships/${profileId}/policies/${request.policyId}/nudge`);
        return;
      }
      const policy = request.policyId ? policyById.get(request.policyId) : null;
      const clientId = clientIdForRequest(request, policy);
      if (!clientId) return;
      navigate(`/resend-invitation?profileId=${profileId}&clientId=${clientId}&mode=resend`);
    },
    [navigate, profileId, policyById],
  );

  // Requests still waiting on a client signature — drives the direct client
  // nudge below.
  const clientNudgeRequests = useMemo(
    () => requests.filter((r) => deriveDisplayStatus(r.status, r.actions) === 'PENDING_CLIENT'),
    [requests],
  );

  // Nudging a client always goes direct — no chooser. The resend email bundles
  // every outstanding signature for a client, so there's one nudge per client.
  // When pending signatures span multiple distinct clients we nudge them in
  // sequence (primary first); the trailing ids ride along in a `nextClients`
  // queue the resend page advances on each successful send.
  const handleNudgeClients = useCallback(async () => {
    const seen = new Set<string>();
    const clientIds: string[] = [];
    for (const r of clientNudgeRequests) {
      const policy = r.policyId ? policyById.get(r.policyId) : null;
      const clientId = clientIdForRequest(r, policy);
      if (clientId && !seen.has(clientId)) {
        seen.add(clientId);
        clientIds.push(clientId);
      }
    }
    if (clientIds.length === 0) return;

    let ordered = clientIds;
    if (clientIds.length > 1) {
      try {
        const primary = await clientApi.getPrimaryClient(profileId);
        if (primary?.id && clientIds.includes(primary.id)) {
          ordered = [primary.id, ...clientIds.filter((id) => id !== primary.id)];
        }
      } catch {
        // Primary lookup failed — fall back to request order.
      }
    }

    const [first, ...rest] = ordered;
    const queue = rest.length ? `&nextClients=${rest.join(',')}` : '';
    navigate(`/resend-invitation?profileId=${profileId}&clientId=${first}&mode=resend${queue}`);
  }, [navigate, profileId, policyById, clientNudgeRequests]);

  // Nudging providers opens the chooser. Only PENDING_PROVIDER rows are
  // selectable (a provider nudge requires a completed LOA on that policy); the
  // advisor picks the request, then lands on the standard provider email page
  // whose link targets that request.
  const handleNudgeProviders = useCallback(() => {
    setNudgeMode('PROVIDER');
  }, []);

  const columns = useMemo<LOATableColumn<ProfileRequestDTO>[]>(
    () => [
      {
        key: 'provider',
        label: 'Provider',
        render: (r) => {
          if (!r.policyId) {
            // Policy-less typed requests (New Business) carry their provider on
            // the request itself; only fall back to "Profile-only" when absent.
            if (!r.requestProviderName) {
              return <span style={{ color: '#6b7280' }}>Profile-only</span>;
            }
            return (
              <div className="policy-provider">
                <div className="policy-provider__avatar">
                  {r.requestProviderName
                    .split(' ')
                    .filter(Boolean)
                    .slice(0, 2)
                    .map((w) => w[0])
                    .join('')
                    .toUpperCase()}
                </div>
                <span className="policy-provider__name">{r.requestProviderName}</span>
              </div>
            );
          }
          const policy = policyById.get(r.policyId);
          if (!policy) {
            // Policy id is on the Request but the parent page hasn't loaded
            // it (or it's outside the current page). Fall back to the raw id.
            return (
              <span style={{ color: '#6b7280', fontFamily: 'monospace', fontSize: 12 }}>
                {r.policyId.slice(0, 8)}…
              </span>
            );
          }
          const providerName = policy.providerName || policy.carrier || 'Unknown';
          return (
            <div className="policy-provider">
              <div className="policy-provider__avatar">
                {policy.providerLogoUrl ? (
                  <img src={policy.providerLogoUrl} alt={providerName} />
                ) : (
                  providerName
                    .split(' ')
                    .filter(Boolean)
                    .slice(0, 2)
                    .map((w) => w[0])
                    .join('')
                    .toUpperCase()
                )}
              </div>
              <span className="policy-provider__name">{providerName}</span>
            </div>
          );
        },
      },
      {
        key: 'policyType',
        label: 'Policy Type',
        render: (r) => {
          const policy = r.policyId ? policyById.get(r.policyId) : null;
          const label = policy?.policyTypeName || policy?.category || r.requestProductName;
          return <span className="policy-type">{label || '—'}</span>;
        },
      },
      {
        key: 'policyNumber',
        label: 'Policy No.',
        render: (r) => {
          const policy = r.policyId ? policyById.get(r.policyId) : null;
          if (!r.policyId || !policy) return <span className="policy-number">—</span>;
          return <span className="policy-number">{policy.policyNumber || '—'}</span>;
        },
      },
      {
        key: 'ownershipType',
        label: 'Type',
        render: (r) => {
          const policy = r.policyId ? policyById.get(r.policyId) : null;
          if (!policy) return <span>—</span>;
          return <span>{ownershipLabel(policy.ownershipType)}</span>;
        },
      },
      {
        key: 'signatory',
        label: 'Signatory',
        render: (r) => {
          const policy = r.policyId ? policyById.get(r.policyId) : null;
          return <span>{signatoryNamesFor(r, policy)}</span>;
        },
      },
      {
        key: 'lastUpdated',
        label: 'Last updated',
        render: (r) => (
          <span className="loa-created-date">{formatLastUpdated(r.updatedAt ?? r.createdAt)}</span>
        ),
      },
      {
        key: 'type',
        label: 'Request type',
        render: (r) => <RequestTypeBadge type={r.requestType} />,
        filter: {
          value: typeFilter ?? '',
          ariaLabel: 'Filter by request type',
          options: [
            { value: '', label: 'All types' },
            { value: 'LOA', label: 'LOA' },
            { value: 'COA', label: 'CoA' },
            { value: 'AML_DOCUMENTATION', label: 'AML' },
          ],
          onChange: (v) => setTypeFilter((v || undefined) as ProfileRequestType | undefined),
        },
      },
      {
        key: 'status',
        label: 'Status',
        // Same status pill as the dashboard Request Overview — the
        // PENDING_CLIENT / PENDING_PROVIDER split is derived from the
        // request's status + ordered actions (mirrors the backend).
        render: (r) => <RequestDisplayStatusBadge status={deriveDisplayStatus(r.status, r.actions)} />,
      },
    ],
    [typeFilter, policyById]
  );

  const actions = useMemo<LOATableAction<ProfileRequestDTO>[]>(
    () => [
      {
        key: 'review-aml',
        icon: <EyeIcon />,
        label: 'Review AML documents',
        // AML rows route to the target client's AML page, where each
        // uploaded document is reviewed (approve / reject). The target
        // client rides on the request's actions.
        onClick: (r) => {
          const clientId = r.actions.find((a) => a.assignedActorRefId)?.assignedActorRefId;
          if (clientId) navigate(`/clients/${clientId}/aml`);
        },
        visible: (r) =>
          r.requestType === 'AML_DOCUMENTATION'
          && r.actions.some((a) => a.assignedActorRefId),
      },
      {
        key: 'view-rejection',
        icon: <EyeIcon />,
        label: 'View rejection reason',
        // Only REJECTED rows expose the eye — it opens the provider's
        // rejection justification (reason code label + additional information).
        onClick: (r) => { void onViewRejection(r); },
        visible: (r) => deriveDisplayStatus(r.status, r.actions) === 'REJECTED',
      },
      {
        key: 'view-policy',
        icon: <EyeIcon />,
        label: 'View policy details',
        // Only COMPLETE (accepted) rows with a policy expose the eye — it
        // routes to the policy detail page so the advisor can review the data
        // the provider returned.
        onClick: (r) => onViewPolicy(r),
        visible: (r) => deriveDisplayStatus(r.status, r.actions) === 'COMPLETE' && !!r.policyId,
      },
      {
        key: 'delete',
        icon: <TrashIcon />,
        label: 'Delete',
        // Opens the red confirmation modal; the void(id) call runs on confirm
        // (handleVoidConfirm) after the advisor enters a reason. No hard-delete
        // endpoint exists — void marks the request VOIDED.
        onClick: (r) => onVoid(r),
        visible: (r) => !r.voidedAt,
      },
    ],
    [onVoid, onViewRejection, onViewPolicy, navigate]
  );

  return (
    <div data-testid="requests-tab">
      {showSuccessBanner && (
        <output
          data-testid="requests-sent-banner"
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: 12,
            padding: 16,
            marginBottom: 16,
            borderRadius: 8,
            background: '#EAFAEF',
            color: '#004E24',
            fontSize: 14,
          }}
        >
          <AppIcon name="tick circle" size={20} color="#004E24" />
          <span style={{ flex: 1 }}>Requests sent successfully!</span>
          <button
            type="button"
            onClick={() => setShowSuccessBanner(false)}
            aria-label="Dismiss"
            style={{
              background: 'transparent',
              border: 'none',
              cursor: 'pointer',
              color: '#004E24',
              fontSize: 16,
              lineHeight: 1,
            }}
          >
            ×
          </button>
        </output>
      )}

      <div className="requests-table">
      <LOATableCard
        data={filteredRequests}
        columns={columns}
        actions={actions}
        keyExtractor={(r) => r.id}
        loading={loading}
        error={error}
        onRetry={() => void refresh()}
        title="Requests"
        aboveTable={
          <div className="flex items-center justify-between gap-md px-md pb-lg">
            <div className="flex-1 max-w-[700px]">
              <SearchBar
                value={searchQuery}
                onChange={setSearchQuery}
                placeholder="Search by request type or policy type, number or client name"
              />
            </div>
            <div className="flex items-center gap-md">
              <NudgeOutstandingButton
                onNudgeClients={() => void handleNudgeClients()}
                onNudgeProviders={handleNudgeProviders}
              />
              <Button
                variant="filled"
                size="lg"
                onClick={() => navigate(`/relationships/${profileId}/requests/new`)}
                data-testid="new-request-button"
              >
                New Request
              </Button>
            </div>
          </div>
        }
        emptyMessage="No recent activity to show"
        emptyFilteredMessage="No requests match the current filters. Clear the Type or Status filter to see all requests."
      />
      </div>

      <ChooseRequestModal
        open={nudgeMode !== null}
        initialMode={nudgeMode ?? 'CLIENT'}
        requests={requests}
        policyById={policyById}
        onClose={() => setNudgeMode(null)}
        onSelect={handleNudgeSelect}
      />

      <RejectionReasonModal
        open={rejectionModal !== null}
        onClose={() => setRejectionModal(null)}
        reason={rejectionModal?.reason}
        additionalInformation={rejectionModal?.additionalInformation}
        documentLabel={rejectionModal?.documentLabel}
      />

      <VoidRequestModal
        open={voidTarget !== null}
        requestType={voidTarget?.requestType}
        onClose={() => setVoidTarget(null)}
        onConfirm={handleVoidConfirm}
      />
    </div>
  );
};
